import { FeatureFlag } from '@ps/web-core-enums';

import { RequestError } from '@swf/core/errors/RequestError';
import type { WidgetEffectParams } from '@swf/core/store';
import { authChange } from '@swf/core/utils/actions';
import { sportsUrlParamChange } from '@swf/sports/utils/actions';
import buildSportsRequest from '@swf/sports/utils/buildSportsRequest';
import { getWidgetCancellationSignal } from '@swf/sports/utils/getWidgetCancellationSignal';
import { hasRequestAborted } from '@swf/sports/utils/hasRequestAborted';

import type { SportsCmsBannerWidgetConfig } from '../config';

import type { CmsBannerSuccessResponse, WidgetDispatch, WidgetSelect } from './types';

export const effects = {
  async preload(_payload: undefined, params: WidgetEffectParams<SportsCmsBannerWidgetConfig>) {
    const { dispatch, widget, select, rootState } = params;

    const { hasPreloaded }: WidgetSelect = select[widget.uid];
    const { cmsBannerRequested, clear }: WidgetDispatch = dispatch[widget.uid];

    sportsUrlParamChange(params, 'eventTypeId')?.subscribe({
      next: () => cmsBannerRequested(),
      complete: () => clear()
    });

    authChange(params.actions)?.subscribe({
      next: () => cmsBannerRequested(),
      complete: () => clear()
    });

    if (!hasPreloaded(rootState)) {
      await cmsBannerRequested();
    }
  },

  async cmsBannerRequested(
    _payload: undefined,
    params: WidgetEffectParams<SportsCmsBannerWidgetConfig>
  ) {
    const { request, dispatch, rootState, select, widget } = params;
    const { eventTypeId = '' } = select.sports.getUrlParams(rootState);
    const alias = select.cms.getPage(rootState)?.alias || select.cms.getActiveAlias(rootState);

    const sportsRequest = buildSportsRequest(request, rootState);
    const { dataLoadSuccess, dataLoadFailed }: WidgetDispatch = dispatch[widget.uid];

    try {
      const path =
        alias === 'sportsHome'
          ? ('/cms-banners/home' as const)
          : (`/cms-banners/sport/${eventTypeId}` as const);

      const {
        body: { data }
      } = await sportsRequest<CmsBannerSuccessResponse>({
        path,
        signal: getWidgetCancellationSignal(params),
        includeFeatures: [FeatureFlag.ISPEnableStarSub]
      });

      dataLoadSuccess(data);
    } catch (error) {
      if (error instanceof RequestError) {
        if (hasRequestAborted(error)) return;
        dataLoadFailed(error);
      }
    }
  }
};
